/**
 * Desktop fits its bounded frame to the visual viewport. Compact touch layouts
 * keep document scrolling and consume only the fixed-control offsets in CSS.
 * Neither keyboard transitions nor viewport panning remount the canvas/input.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import {
  IDLE_VIEWPORT_LIFT,
  resolveViewportLift,
  viewportLiftVariables,
  type StageViewportVariables,
  type ViewportLiftState,
} from './stage-viewport.ts';

export function useStageViewport(): {
  style: CSSProperties & StageViewportVariables;
  compact: boolean;
} {
  const [viewportState, setViewportState] = useState<ViewportLiftState>(IDLE_VIEWPORT_LIFT);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) {
      return;
    }
    const update = () => {
      const next = resolveViewportLift({
        windowInnerHeight: window.innerHeight,
        visualViewportHeight: viewport.height,
        visualViewportOffsetTop: viewport.offsetTop,
        visualViewportScale: viewport.scale,
      });
      setViewportState((previous) =>
        previous.height === next.height &&
        previous.offset === next.offset &&
        previous.bottomInset === next.bottomInset &&
        previous.compact === next.compact
          ? previous
          : next,
      );
    };
    update();
    window.addEventListener('resize', update);
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => {
      window.removeEventListener('resize', update);
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
    };
  }, []);

  return { style: viewportLiftVariables(viewportState), compact: viewportState.compact };
}
